import { useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import Header from '../components/Header'

// daily-checkin v2 — vitais (humor/energia/sono/exercício) + relato + treino ESTRUTURADO + PR.
// Grava em diario_humor + diario_entradas + ares_treinos (exercicios jsonb) + ares_marcos.
// Mesma tabela cloud (jwugagzmhkfoxypfnlor) que o ARES lê → fonte única. Template = treino-atual.md.
const CHECKIN_TOKEN = 'eod-k7m2x9'

// A API tem DUAS origens vivas e o mesmo build roda nas duas:
//   público (Hostinger + Cloudflare Worker) → /api-checkin
//   container da vps2b (nginx)              → /ckapi/checkin
// Tenta /ckapi uma vez; se a origem não tiver essa rota (404), memoriza /api-checkin pelo resto da sessão.
// Um POST que volta 404 não gravou nada, então repetir o corpo no fallback é seguro.
let CK_BASE: string | null = null
async function ckFetch(init?: RequestInit): Promise<Response> {
  const q = '?k=' + encodeURIComponent(CHECKIN_TOKEN)
  if (CK_BASE) return fetch(CK_BASE + q, init)
  try {
    const r = await fetch('/ckapi/checkin' + q, init)
    if (r.status !== 404) { CK_BASE = '/ckapi/checkin'; return r }
  } catch { /* origem sem /ckapi → cai pro worker */ }
  CK_BASE = '/api-checkin'
  return fetch(CK_BASE + q, init)
}

// Contador de 90 dias. Chave neutra no banco de proposito (quem olhar por cima do ombro nao le nada).
// O numero e o placar, mas o campo que importa e a NOTA do dia que quebra: gatilho (hora/onde/depois de que).
const FOCO_KEY = 'foco90'
const FOCO_META = 90

const TREINO_TIPOS = ['A', 'B', 'C', 'D', 'deload', 'karate', 'cardio', 'livre']
// rótulo do botão (o valor gravado continua A/B/C/D; o CHECK do banco ganhou 'D' em 2026-10-20)
const TIPO_LABEL: Record<string, string> = { A: 'A · sup', B: 'B · inf', C: 'C · sup', D: 'D · inf' }
const STRENGTH = new Set(['A', 'B', 'C', 'D', 'deload']) // tipos de força: entram no bloco/semana
const WITH_EX = STRENGTH                                 // tipos que mostram a grade de exercícios
const PR_TIPOS = ['1RM', '3RM', '5RM', '8RM']

// Template por tipo (fonte: kb/ares/treino-atual.md, bloco 2 Superior/Inferior 4x, sync 2026-10-20).
// carga: ponto de partida quando o exercício nunca foi registrado. Se já foi, tplRows usa a ÚLTIMA carga logada.
type TplEx = { nome: string; series: string; reps: string; rpe: string; carga?: string }
const TREINO_TEMPLATES: Record<string, TplEx[]> = {
  // Seg · Superior Força (karatê à noite: zero perna)
  A: [
    { nome: 'Flexão explosiva', series: '3', reps: '5', rpe: '' },
    { nome: 'Supino reto barra', series: '4', reps: '5-6', rpe: '7' },
    { nome: 'Remada curvada', series: '4', reps: '6-8', rpe: '7' },
    { nome: 'Desenvolvimento militar barra (em pé)', series: '3', reps: '6-8', rpe: '7' },
    { nome: 'Barra fixa / Puxada', series: '3', reps: '6-10', rpe: '7' },
    { nome: 'Face pull', series: '3', reps: '15', rpe: '' },
    { nome: 'Pallof press', series: '3', reps: '10/lado', rpe: '' },
  ],
  // Ter · Inferior Força + Potência Vertical
  B: [
    { nome: 'Box Jump / Squat Jump', series: '4', reps: '3', rpe: '' },
    { nome: 'Agachamento com barra', series: '4', reps: '5-6', rpe: '7', carga: '75' },
    { nome: 'Terra Romeno', series: '3', reps: '8', rpe: '7' },
    { nome: 'Afundo búlgaro halteres', series: '3', reps: '8/perna', rpe: '7' },
    { nome: 'Panturrilha em pé', series: '3', reps: '12-15', rpe: '8' },
    { nome: 'Crunch com anilha (abdômen)', series: '3', reps: '12-15', rpe: '' },
  ],
  // Qui · Superior Volume + Braço
  C: [
    { nome: 'Arremesso med ball no peito', series: '3', reps: '5', rpe: '' },
    { nome: 'Supino inclinado halteres', series: '3', reps: '8-10', rpe: '8', carga: '20' },
    { nome: 'Puxada frente pegada neutra', series: '3', reps: '10-12', rpe: '8', carga: '45' },
    { nome: 'Remada unilateral halter', series: '3', reps: '10/lado', rpe: '8' },
    { nome: 'Elevação lateral', series: '4', reps: '12-15', rpe: '8' },
    { nome: 'Rosca direta halter/polia', series: '3', reps: '10-12', rpe: '8' },
    { nome: 'Tríceps corda', series: '3', reps: '12-15', rpe: '8' },
    { nome: 'Voador invertido (máquina)', series: '3', reps: '15', rpe: '' },
  ],
  // Sex · Inferior Posterior + Potência Lateral
  D: [
    { nome: 'Skater Jumps / Med Ball rotacional', series: '3', reps: '4-5/lado', rpe: '' },
    { nome: 'Hip thrust barra', series: '4', reps: '6-8', rpe: '7' },
    { nome: 'Leg Press', series: '3', reps: '10-12', rpe: '8', carga: '200' },
    { nome: 'Leg Curl', series: '3', reps: '10-12', rpe: '8' },
    { nome: 'Agachamento cossack goblet', series: '2', reps: '6/lado', rpe: '' },
    { nome: 'Woodchopper cabo', series: '3', reps: '12/lado', rpe: '' },
    { nome: 'Abdominal russo com peso', series: '3', reps: '15/lado', rpe: '' },
  ],
  deload: [],
}

// Hábitos diários de checar com 1 toque (diario_habitos, mesmo upsert do foco90).
// Chave curta e estável: é o que o ARES e a Psique leem no banco.
const HABITOS_DIA: { key: string; label: string }[] = [
  { key: 'creatina', label: 'Creatina' },
  { key: 'whey', label: 'Whey' },
  { key: 'estudo-karate', label: 'Kihon 10min' },
]

interface Humor { data: string; nivel: number | null; energia: number | null; sono_horas: number | null; exercicio_horas: number | null; nota: string | null; created_at: string }
interface Entrada { data: string; relato: string; alimentacao: string | null; acao_do_dia: string | null; ancora: string | null; created_at: string }
interface ExJson { nome: string; series: number | null; reps: string | null; carga_kg: number | null; rpe: number | null }
interface TreinoRow { id: string; data: string; dia_tipo: string | null; bloco_semana: number | null; semana_num: number | null; exercicios: ExJson[] | null; rpe_medio: number | null; volume_total: number | null; calorias: number | null; observacoes: string | null; created_at: string }
interface Marco { data: string; exercicio: string; tipo: string; valor: number; unidade: string | null; contexto: string | null; created_at: string }
interface HabitoRow { data: string; habito: string; concluido: boolean; nota: string | null; created_at: string }

// estado editável do form
interface ExRow { nome: string; series: string; reps: string; carga_kg: string; rpe: string }
interface Sessao { id?: string; dia_tipo: string; exercicios: ExRow[]; obs: string; calorias: string }

const fade = (delay = 0) => ({ initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.4, delay } })

function todayStr() {
  const d = new Date() // data LOCAL (não UTC) — evita rolar pro dia seguinte à noite no Brasil (UTC-3)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
function shiftDay(d: string, n: number) { const dt = new Date(d + 'T12:00:00'); dt.setDate(dt.getDate() + n); return dt.toISOString().slice(0, 10) }
function fmtDay(d: string) {
  const dt = new Date(d + 'T12:00:00')
  const dia = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'][dt.getDay()]
  return `${dia} ${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}`
}
function num(v: string): number | null { const n = parseFloat(v); return isNaN(n) ? null : n }
// mapa dia -> concluido do habito. Vem ordenado desc, entao o 1o registro de cada dia e o mais recente.
function focoMap(habitos: HabitoRow[], key: string = FOCO_KEY): Map<string, boolean> {
  const m = new Map<string, boolean>()
  habitos.forEach(h => {
    if (h.habito !== key) return
    const d = (h.data || '').slice(0, 10)
    if (!m.has(d)) m.set(d, !!h.concluido)
  })
  return m
}
// sequencia de dias limpos terminando em hoje. Hoje AINDA nao marcado nao zera a conta (o dia nao acabou),
// a contagem so para num dia marcado como quebra ou num buraco anterior a hoje.
// FOCO 90 (regra do Matheus, 26/10/2026): conta os dias marcados LIMPOS desde o último escorregão.
// Dia sem marca NÃO soma e NÃO zera, a contagem só fica parada. Só "escorreguei" (concluido=false) volta a zero.
// semMarca = dias em branco desde o último escorregão (ou desde a 1ª marca), sem contar hoje, que ainda não acabou.
function focoContagem(m: Map<string, boolean>, hoje: string): { limpos: number; semMarca: number } {
  const dias = [...m.keys()].sort()
  if (!dias.length) return { limpos: 0, semMarca: 0 }
  let limpos = 0, semMarca = 0
  for (let d = hoje; d >= dias[0]; d = shiftDay(d, -1)) {
    const v = m.get(d)
    if (v === false) break
    if (v === true) limpos++
    else if (d !== hoje) semMarca++
  }
  return { limpos, semMarca }
}
function focoStreak(m: Map<string, boolean>, hoje: string): number {
  let d = m.has(hoje) ? hoje : shiftDay(hoje, -1)
  let n = 0
  while (m.get(d) === true) { n++; d = shiftDay(d, -1) }
  return n
}
function repsLow(reps: string): number | null { const m = String(reps).match(/\d+/); return m ? parseInt(m[0]) : null }
function blankEx(): ExRow { return { nome: '', series: '', reps: '', carga_kg: '', rpe: '' } }
// carga sugerida = a ÚLTIMA registrada daquele exercício (treinos vem desc), senão o ponto de partida do template.
// É isso que faz o template andar sozinho com a progressão, sem editar código a cada semana.
function lastCarga(treinos: { exercicios: ExJson[] | null }[], nome: string): number | null {
  const k = nome.trim().toLowerCase()
  for (const t of treinos) for (const e of t.exercicios || []) {
    if ((e.nome || '').trim().toLowerCase() === k && e.carga_kg != null) return e.carga_kg
  }
  return null
}
function tplRows(tipo: string, treinos: { exercicios: ExJson[] | null }[] = []): ExRow[] {
  const tpl = TREINO_TEMPLATES[tipo]
  if (!tpl || !tpl.length) return [blankEx()]
  return tpl.map(e => {
    const last = lastCarga(treinos, e.nome)
    return { nome: e.nome, series: e.series, reps: e.reps, carga_kg: last != null ? String(last) : (e.carga || ''), rpe: e.rpe }
  })
}
function sessaoMetrics(ex: ExRow[]) {
  const rpes = ex.map(e => num(e.rpe)).filter((n): n is number => n != null)
  const rpe_medio = rpes.length ? Math.round((rpes.reduce((a, b) => a + b, 0) / rpes.length) * 10) / 10 : null
  let vol = 0
  ex.forEach(e => { const s = num(e.series) || 0, r = repsLow(e.reps) || 0, c = num(e.carga_kg) || 0; vol += s * r * c })
  return { rpe_medio, volume_total: vol > 0 ? vol : null }
}
function fmtTreino(t: TreinoRow): string {
  const exs = (t.exercicios || []).map(e => {
    const carga = e.carga_kg != null ? `${e.carga_kg}kg ` : ''
    const sr = e.series != null && e.reps ? `${e.series}×${e.reps}` : (e.reps || '')
    return `${e.nome} ${carga}${sr}`.trim()
  })
  return exs.length ? exs.join(' · ') : (t.observacoes || '')
}

// ── persistência local ──
// 1) cache do histórico → render instantâneo no 1º paint (igual /stats).
// 2) rascunho por dia → o relato/vitais que você começou a escrever sobrevivem a fechar a aba sem salvar.
const CK_CACHE = 'ck_cache_v1'
function readCkCache(): { humor?: Humor[]; entradas?: Entrada[]; treinos?: TreinoRow[]; marcos?: Marco[]; habitos?: HabitoRow[] } | null {
  try { return JSON.parse(localStorage.getItem(CK_CACHE) || 'null') } catch { return null }
}
function writeCkCache(d: unknown) { try { localStorage.setItem(CK_CACHE, JSON.stringify(d)) } catch { /* quota */ } }

type Snap = {
  nivel: number | null; energia: number | null; sono: string; exercicio: string; nota: string
  relato: string; alimentacao: string; acao: string; ancora: string
  // treino/PR em montagem: só rascunho local (não vêm do servidor, que é append por dia_tipo)
  bloco: string; semana: string; sessoes: Sessao[]
  prOpen: boolean; prEx: string; prTipo: string; prValor: string; prCtx: string
}
const BLANK_SNAP: Snap = { nivel: null, energia: null, sono: '', exercicio: '', nota: '', relato: '', alimentacao: '', acao: '', ancora: '', bloco: '', semana: '', sessoes: [], prOpen: false, prEx: '', prTipo: '1RM', prValor: '', prCtx: '' }
function draftKey(d: string) { return 'ck_draft_' + d }
// merge com BLANK_SNAP: rascunhos antigos (sem campos de treino) não quebram o shape.
function readDraft(d: string): Snap | null { try { const s = JSON.parse(localStorage.getItem(draftKey(d)) || 'null'); return s ? { ...BLANK_SNAP, ...s } : null } catch { return null } }
function writeDraft(d: string, s: Snap) { try { localStorage.setItem(draftKey(d), JSON.stringify(s)) } catch { /* quota */ } }
function clearDraft(d: string) { try { localStorage.removeItem(draftKey(d)) } catch { /* noop */ } }
function snapBlank(s: Snap) { return s.nivel == null && s.energia == null && !s.sono && !s.exercicio && !s.nota && !s.relato.trim() && !s.alimentacao && !s.acao && !s.ancora && s.sessoes.length === 0 && !s.bloco && !s.semana && !s.prEx && !s.prValor && !s.prCtx }
function snapEq(a: Snap, b: Snap) { return a.nivel === b.nivel && a.energia === b.energia && a.sono === b.sono && a.exercicio === b.exercicio && a.nota === b.nota && a.relato === b.relato && a.alimentacao === b.alimentacao && a.acao === b.acao && a.ancora === b.ancora && a.bloco === b.bloco && a.semana === b.semana && a.prOpen === b.prOpen && a.prEx === b.prEx && a.prTipo === b.prTipo && a.prValor === b.prValor && a.prCtx === b.prCtx && JSON.stringify(a.sessoes) === JSON.stringify(b.sessoes) }
function serverSnap(e?: Entrada, h?: Humor): Snap {
  return {
    nivel: h?.nivel ?? null,
    energia: h?.energia ?? null,
    sono: h?.sono_horas != null ? String(h.sono_horas) : '',
    exercicio: h?.exercicio_horas != null ? String(h.exercicio_horas) : '',
    nota: h?.nota || '',
    relato: e?.relato || '',
    alimentacao: e?.alimentacao || '',
    acao: e?.acao_do_dia || '',
    ancora: e?.ancora || '',
    // treino/PR não têm prefill do servidor (append): só rascunho restaura sessão em montagem
    bloco: '', semana: '', sessoes: [], prOpen: false, prEx: '', prTipo: '1RM', prValor: '', prCtx: '',
  }
}
const sameDay = (s: string, d: string) => (s || '').slice(0, 10) === d
// TreinoRow (do histórico) -> Sessao editável no form (mantém o id p/ o save virar UPDATE, não append)
function treinoToSessao(t: TreinoRow): Sessao {
  return {
    id: t.id,
    dia_tipo: t.dia_tipo || 'livre',
    exercicios: (t.exercicios || []).map(e => ({
      nome: e.nome || '',
      series: e.series != null ? String(e.series) : '',
      reps: e.reps || '',
      carga_kg: e.carga_kg != null ? String(e.carga_kg) : '',
      rpe: e.rpe != null ? String(e.rpe) : '',
    })),
    obs: t.observacoes || '',
    calorias: t.calorias != null ? String(t.calorias) : '',
  }
}

function Scale({ value, onChange, accent }: { value: number | null; onChange: (n: number) => void; accent: string }) {
  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
        <button key={n} onClick={() => onChange(n)} style={{
          width: 32, height: 32, borderRadius: 8, cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600,
          border: '1px solid ' + (value === n ? accent : 'var(--border)'),
          background: value === n ? accent : 'var(--bg2)', color: value === n ? '#0b0b0f' : 'var(--muted)', transition: 'all .12s',
        }}>{n}</button>
      ))}
    </div>
  )
}

const inputStyle: React.CSSProperties = { width: '100%', padding: '0.6rem 0.8rem', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--fg)', fontSize: '0.9rem', fontFamily: 'inherit', boxSizing: 'border-box' }
const labelStyle: React.CSSProperties = { display: 'block', fontSize: '0.72rem', color: 'var(--muted)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '0.5rem', marginTop: '1.25rem' }
const numStyle: React.CSSProperties = { ...inputStyle, width: 90 }
const exNumStyle: React.CSSProperties = { ...inputStyle, padding: '0.45rem 0.4rem', fontSize: '0.82rem', textAlign: 'center' }

export default function Checkin() {
  const [date, setDate] = useState(todayStr())
  const [nivel, setNivel] = useState<number | null>(null)
  const [energia, setEnergia] = useState<number | null>(null)
  const [sono, setSono] = useState('')
  const [exercicio, setExercicio] = useState('')
  const [nota, setNota] = useState('')
  const [relato, setRelato] = useState('')
  const [alimentacao, setAlimentacao] = useState('')
  const [acao, setAcao] = useState('')
  const [ancora, setAncora] = useState('')
  // treino
  const [bloco, setBloco] = useState('')
  const [semana, setSemana] = useState('')
  const [sessoes, setSessoes] = useState<Sessao[]>([])
  // PR
  const [prOpen, setPrOpen] = useState(false)
  const [prEx, setPrEx] = useState('')
  const [prTipo, setPrTipo] = useState('1RM')
  const [prValor, setPrValor] = useState('')
  const [prCtx, setPrCtx] = useState('')
  // dados carregados — cache-first: o histórico já aparece no 1º paint, depois revalida.
  const cache = readCkCache()
  const [humor, setHumor] = useState<Humor[]>(cache?.humor || [])
  const [entradas, setEntradas] = useState<Entrada[]>(cache?.entradas || [])
  const [treinos, setTreinos] = useState<TreinoRow[]>(cache?.treinos || [])
  const [marcos, setMarcos] = useState<Marco[]>(cache?.marcos || [])
  const [habitos, setHabitos] = useState<HabitoRow[]>(cache?.habitos || [])
  const [loaded, setLoaded] = useState<boolean>(cache != null)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  // foco 90 dias
  const [focoNotaOpen, setFocoNotaOpen] = useState(false)
  const [focoNota, setFocoNota] = useState('')
  const [focoSaving, setFocoSaving] = useState(false)

  // controle do prefill/rascunho do dia
  const appliedFor = useRef<string | null>(null)   // dia cujos campos já foram populados
  const baseline = useRef<Snap>(BLANK_SNAP)        // o que carregou (server ou rascunho) p/ saber se o user divergiu
  const injectTreino = useRef<{ date: string; sessao: Sessao; bloco: number | null; semana: number | null } | null>(null) // treino clicado no histórico p/ editar
  const applySnap = (s: Snap) => {
    setNivel(s.nivel); setEnergia(s.energia); setSono(s.sono); setExercicio(s.exercicio)
    setNota(s.nota); setRelato(s.relato); setAlimentacao(s.alimentacao); setAcao(s.acao); setAncora(s.ancora)
    setBloco(s.bloco); setSemana(s.semana); setSessoes(s.sessoes)
    setPrOpen(s.prOpen); setPrEx(s.prEx); setPrTipo(s.prTipo); setPrValor(s.prValor); setPrCtx(s.prCtx)
    baseline.current = s
  }

  function load() {
    ckFetch()
      .then(r => { if (!r.ok) throw new Error('http ' + r.status); return r.json() })
      .then((d: { humor?: Humor[]; entradas?: Entrada[]; treinos?: TreinoRow[]; marcos?: Marco[]; habitos?: HabitoRow[]; partial?: boolean }) => {
        // resposta incompleta (Supabase deu hiccup) → NÃO sobrescreve; mantém o cache/estado atual.
        if (d && !d.partial) {
          setHumor(d.humor || []); setEntradas(d.entradas || []); setTreinos(d.treinos || []); setMarcos(d.marcos || []); setHabitos(d.habitos || [])
          writeCkCache(d)
        }
        setLoaded(true)
      }).catch(() => setLoaded(true))
  }
  useEffect(load, [])

  // ── app instalável (iPhone: Compartilhar → Adicionar à Tela de Início) ──
  // A casca é o index.html do site inteiro, então troca manifest/ícone/meta só enquanto o /checkin está aberto.
  // O app da tela inicial do iOS tem localStorage SEPARADO do Safari: sem o ?k= na URL ele abriria trancado
  // e o gate mandaria pra home. Por isso a URL do /checkin sempre carrega o ?k= (o token já está no bundle público).
  useEffect(() => {
    const url = new URL(window.location.href)
    if (url.searchParams.get('k') !== CHECKIN_TOKEN) {
      url.searchParams.set('k', CHECKIN_TOKEN)
      window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash)
    }
    const undo: (() => void)[] = []
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`)
      if (el) { const prev = el.href; const e = el; undo.push(() => { e.href = prev }) }
      else { el = document.createElement('link'); el.rel = rel; document.head.appendChild(el); const e = el; undo.push(() => e.remove()) }
      el.href = href
    }
    const setMeta = (name: string, content: string) => {
      let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)
      if (el) { const prev = el.content; const e = el; undo.push(() => { e.content = prev }) }
      else { el = document.createElement('meta'); el.name = name; document.head.appendChild(el); const e = el; undo.push(() => e.remove()) }
      el.content = content
    }
    setLink('manifest', '/manifest-checkin.json')
    setLink('apple-touch-icon', '/checkin-icon-180.png')
    setMeta('apple-mobile-web-app-capable', 'yes')
    setMeta('mobile-web-app-capable', 'yes')
    setMeta('apple-mobile-web-app-title', 'Check-in')
    setMeta('apple-mobile-web-app-status-bar-style', 'black')
    setMeta('theme-color', '#0b0b0f')
    const prevTitle = document.title
    document.title = 'Check-in'
    undo.push(() => { document.title = prevTitle })
    return () => { undo.reverse().forEach(f => f()) }
  }, [])

  // prefill do dia: ao abrir / trocar de dia, popula os campos com o RASCUNHO local ou, na falta dele,
  // com o que já está salvo naquele dia. É isso que faz "abriu → o relato que eu tava escrevendo tá lá".
  useEffect(() => {
    const e = entradas.find(x => sameDay(x.data, date))
    const h = humor.find(x => sameDay(x.data, date))
    if (appliedFor.current !== date) {
      const draft = readDraft(date)
      let base = draft || serverSnap(e, h)
      if (injectTreino.current && injectTreino.current.date === date) {
        const it = injectTreino.current
        base = { ...base, sessoes: [it.sessao], bloco: it.bloco != null ? String(it.bloco) : base.bloco, semana: it.semana != null ? String(it.semana) : base.semana }
        injectTreino.current = null
      }
      applySnap(base)
      appliedFor.current = date
    } else if (snapEq(baseline.current, BLANK_SNAP) && (e || h) && !readDraft(date)) {
      // cache estava frio: mostramos vazio e o fetch trouxe o dia agora. Só preenche se o user não começou a digitar.
      const cur: Snap = { nivel, energia, sono, exercicio, nota, relato, alimentacao, acao, ancora, bloco, semana, sessoes, prOpen, prEx, prTipo, prValor, prCtx }
      if (snapBlank(cur)) applySnap(serverSnap(e, h))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, entradas, humor])

  // autosave do rascunho do dia — cada tecla persiste; sobrevive a fechar a aba sem salvar.
  useEffect(() => {
    if (appliedFor.current !== date) return
    const s: Snap = { nivel, energia, sono, exercicio, nota, relato, alimentacao, acao, ancora, bloco, semana, sessoes, prOpen, prEx, prTipo, prValor, prCtx }
    if (snapEq(s, baseline.current)) return       // ainda igual ao que carregou → não cria rascunho à toa
    if (snapBlank(s)) { clearDraft(date); return }
    writeDraft(date, s)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nivel, energia, sono, exercicio, nota, relato, alimentacao, acao, ancora, bloco, semana, sessoes, prOpen, prEx, prTipo, prValor, prCtx])

  // mutadores de sessão / exercício
  const addSessao = (tipo: string) => setSessoes(s => [...s, { dia_tipo: tipo, exercicios: WITH_EX.has(tipo) ? tplRows(tipo, treinos) : [], obs: '', calorias: '' }])
  const removeSessao = (i: number) => setSessoes(s => s.filter((_, idx) => idx !== i))
  const patchSessao = (i: number, patch: Partial<Sessao>) => setSessoes(s => s.map((x, idx) => idx === i ? { ...x, ...patch } : x))
  const addEx = (si: number) => setSessoes(s => s.map((x, idx) => idx === si ? { ...x, exercicios: [...x.exercicios, blankEx()] } : x))
  const removeEx = (si: number, ei: number) => setSessoes(s => s.map((x, idx) => idx === si ? { ...x, exercicios: x.exercicios.filter((_, j) => j !== ei) } : x))
  const patchEx = (si: number, ei: number, patch: Partial<ExRow>) => setSessoes(s => s.map((x, idx) => idx === si ? { ...x, exercicios: x.exercicios.map((e, j) => j === ei ? { ...e, ...patch } : e) } : x))

  // clicar num treino do histórico -> carrega ele no form (lá em cima) pra editar; save vira UPDATE (tem id)
  const editTreino = (t: TreinoRow) => {
    const day = (t.data || '').slice(0, 10)
    const sessao = treinoToSessao(t)
    if (day === date) {
      setSessoes([sessao])
      if (t.bloco_semana != null) setBloco(String(t.bloco_semana))
      if (t.semana_num != null) setSemana(String(t.semana_num))
    } else {
      injectTreino.current = { date: day, sessao, bloco: t.bloco_semana, semana: t.semana_num }
      setDate(day)
    }
    try { window.scrollTo({ top: 0, behavior: 'smooth' }) } catch { /* noop */ }
  }

  const hasStrength = sessoes.some(s => STRENGTH.has(s.dia_tipo))

  const dayOf = (s: string) => (s || '').slice(0, 10)
  const dh = humor.filter(h => dayOf(h.data) === date)
  const de = entradas.filter(e => dayOf(e.data) === date)
  const dtreinos = treinos.filter(t => dayOf(t.data) === date)
  const isToday = date === todayStr()
  const savedTxt = [
    dh.length ? `${dh.length} vital${dh.length > 1 ? 'is' : ''}` : '',
    de.length ? `${de.length} relato${de.length > 1 ? 's' : ''}` : '',
    dtreinos.length ? `${dtreinos.length} treino${dtreinos.length > 1 ? 's' : ''}` : '',
  ].filter(Boolean).join(' · ')

  // ── foco 90 dias ──
  // A sequencia e sempre contada a partir de HOJE (é o estado atual), mas o clique marca o DIA ABERTO,
  // o que permite preencher um dia pra trás sem mentir sobre a sequência corrente.
  const fMap = focoMap(habitos)
  const { limpos: fStreak, semMarca: fSemMarca } = focoContagem(fMap, todayStr())
  const fDia = fMap.get(date)   // true = limpo · false = quebrou · undefined = não marcado

  async function saveFoco(concluido: boolean | null, nota?: string) {
    setFocoSaving(true); setMsg(null)
    const item = concluido === null
      ? { habito: FOCO_KEY, remover: true }
      : { habito: FOCO_KEY, concluido, nota: nota && nota.trim() ? nota.trim() : null }
    try {
      const r = await ckFetch({
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: date, habitos: [item] }),
      })
      if (r.ok) {
        setMsg(concluido === null ? '✓ desmarcado' : (concluido ? '✓ dia marcado' : '✓ anotado'))
        setFocoNotaOpen(false); setFocoNota('')
        load()
      } else {
        const d = await r.json().catch(() => ({}))
        setMsg('erro: ' + (d.error || 'falhou'))
      }
    } catch { setMsg('erro de rede') }
    setFocoSaving(false)
  }

  // ── hábitos do dia (creatina, whey, kihon) ── toque marca/desmarca o DIA ABERTO; sequência contada a partir de hoje
  const [habSaving, setHabSaving] = useState<string | null>(null)
  async function toggleHabito(key: string) {
    const feito = focoMap(habitos, key).get(date) === true
    setHabSaving(key); setMsg(null)
    try {
      const r = await ckFetch({
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: date, habitos: [feito ? { habito: key, remover: true } : { habito: key, concluido: true, nota: null }] }),
      })
      if (r.ok) load()
      else { const d = await r.json().catch(() => ({})); setMsg('erro: ' + (d.error || 'falhou')) }
    } catch { setMsg('erro de rede') }
    setHabSaving(null)
  }

  async function submit() {
    const treinosPayload = sessoes.filter(s => s.dia_tipo).map(s => {
      const exercicios = s.exercicios.filter(e => e.nome.trim()).map(e => ({
        nome: e.nome.trim(), series: e.series ? parseInt(e.series) : null, reps: e.reps.trim() || null, carga_kg: num(e.carga_kg), rpe: num(e.rpe),
      }))
      const { rpe_medio, volume_total } = sessaoMetrics(s.exercicios)
      const strength = STRENGTH.has(s.dia_tipo)
      return {
        id: s.id,
        dia_tipo: s.dia_tipo,
        bloco_semana: strength && bloco ? parseInt(bloco) : null,
        semana_num: strength && semana ? parseInt(semana) : null,
        exercicios: exercicios.length ? exercicios : null,
        rpe_medio, volume_total,
        calorias: s.calorias ? parseInt(s.calorias) : null,
        observacoes: s.obs.trim() || null,
      }
    })
    const marcosPayload = (prEx.trim() && prValor) ? [{ exercicio: prEx.trim(), tipo: prTipo, valor: num(prValor), unidade: 'kg', contexto: prCtx.trim() || null }] : []

    if (nivel == null && !relato.trim() && !alimentacao.trim() && !sono && !exercicio && !treinosPayload.length && !marcosPayload.length) {
      setMsg('preenche algo (humor, sono, treino ou relato)'); return
    }
    setSaving(true); setMsg(null)
    try {
      const r = await ckFetch({
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: date, nivel, energia, sono_horas: sono, exercicio_horas: exercicio, nota, relato, alimentacao, acao_do_dia: acao, ancora, treinos: treinosPayload, marcos: marcosPayload }),
      })
      const d = await r.json()
      if (r.ok) {
        setMsg('✓ salvo')
        clearDraft(date)            // servidor virou a fonte do dia → descarta o rascunho local
        appliedFor.current = null   // força re-seed do dia com o valor recém-salvo (relato/vitais continuam na tela)
        setSessoes([]); setBloco(''); setSemana('')   // treino/PR são append → resetam
        setPrOpen(false); setPrEx(''); setPrTipo('1RM'); setPrValor(''); setPrCtx('')
        load()
      } else setMsg('erro: ' + (d.error || 'falhou'))
    } catch { setMsg('erro de rede') }
    setSaving(false)
  }

  return (
    <>
      <Header />
      <main style={{ maxWidth: 620, margin: '0 auto', padding: '3rem 1.5rem 6rem' }}>
        <motion.div {...fade(0)} style={{ marginBottom: '1.25rem' }}>
          <p style={{ fontSize: '0.75rem', color: 'var(--muted)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.75rem' }}>Check-in diário</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
            <button onClick={() => setDate(shiftDay(date, -1))} style={navBtn} aria-label="dia anterior">←</button>
            <h1 style={{ fontSize: 'clamp(1.5rem, 5vw, 2.1rem)', fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1.1, minWidth: 150, textAlign: 'center' }}>
              {isToday ? 'Hoje' : fmtDay(date)}
            </h1>
            <button onClick={() => !isToday && setDate(shiftDay(date, 1))} disabled={isToday} style={{ ...navBtn, opacity: isToday ? 0.3 : 1, cursor: isToday ? 'not-allowed' : 'pointer' }} aria-label="próximo dia">→</button>
          </div>
          <p style={{ color: 'var(--muted)', fontSize: '0.85rem', textAlign: 'center' }}>
            {savedTxt ? `já registrado: ${savedTxt}` : (isToday ? 'como foi o dia?' : 'preencher esse dia')}
          </p>
        </motion.div>

        {/* ── FOCO 90 DIAS ── contador clicável; a nota do dia que quebra é o dado que importa */}
        <motion.div {...fade(0.05)} style={{
          background: 'var(--bg2)', border: '1px solid rgba(167,139,250,0.3)', borderRadius: 12,
          padding: '1.1rem 1.25rem', marginBottom: '1rem',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
            <div>
              <p style={{ fontSize: '0.72rem', color: 'var(--muted)', letterSpacing: '0.1em', textTransform: 'uppercase', margin: 0 }}>Foco · 90 dias</p>
              <p style={{ fontSize: '0.78rem', color: 'var(--muted)', margin: '0.35rem 0 0' }}>
                {fDia === true ? 'dia marcado' : fDia === false ? 'dia anotado' : (isToday ? 'toque no número' : 'marcar esse dia')}
                {fSemMarca > 0 && <span style={{ display: 'block', marginTop: 2 }}>{fSemMarca} dia{fSemMarca > 1 ? 's' : ''} sem marcar (não conta, não zera)</span>}
              </p>
            </div>
            <button
              onClick={() => saveFoco(fDia === true ? null : true)}
              disabled={focoSaving}
              aria-label={fDia === true ? 'desmarcar o dia' : 'marcar o dia'}
              style={{
                padding: '0.55rem 1.1rem', borderRadius: 10, cursor: focoSaving ? 'wait' : 'pointer',
                border: '1px solid ' + (fDia === true ? '#a78bfa' : 'var(--border)'),
                background: fDia === true ? 'rgba(167,139,250,0.14)' : 'var(--bg2)',
                color: 'var(--fg)', fontFamily: 'inherit', transition: 'all .12s', flexShrink: 0,
              }}>
              <span style={{ fontSize: '1.5rem', fontWeight: 700, letterSpacing: '-0.02em', color: fStreak > 0 ? '#a78bfa' : 'var(--muted)' }}>{fStreak}</span>
              <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--muted)' }}>/{FOCO_META}</span>
            </button>
          </div>

          <div style={{ height: 4, borderRadius: 3, background: 'var(--border)', marginTop: '0.9rem', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: Math.min(100, (fStreak / FOCO_META) * 100) + '%', background: '#a78bfa', transition: 'width .3s' }} />
          </div>

          {!focoNotaOpen && (
            <button onClick={() => { setFocoNotaOpen(true); setFocoNota(habitos.find(h => h.habito === FOCO_KEY && (h.data || '').slice(0, 10) === date)?.nota || '') }}
              style={{ marginTop: '0.75rem', padding: 0, border: 0, background: 'transparent', color: 'var(--muted)', fontSize: '0.78rem', cursor: 'pointer', textDecoration: 'underline', fontFamily: 'inherit' }}>
              {fDia === false ? 'editar o gatilho' : 'escorreguei'}
            </button>
          )}

          {focoNotaOpen && (
            <div style={{ marginTop: '0.8rem' }}>
              <input
                style={{ ...inputStyle, fontSize: '0.85rem' }}
                value={focoNota}
                onChange={e => setFocoNota(e.target.value)}
                placeholder="gatilho: que hora, onde, depois de quê, sentindo o quê"
                autoFocus
              />
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                <button onClick={() => saveFoco(false, focoNota)} disabled={focoSaving} style={{
                  padding: '0.45rem 0.9rem', borderRadius: 8, border: '1px solid rgba(167,139,250,0.4)',
                  background: 'rgba(167,139,250,0.1)', color: 'var(--fg)', fontSize: '0.8rem', fontWeight: 600,
                  cursor: focoSaving ? 'wait' : 'pointer', fontFamily: 'inherit',
                }}>salvar</button>
                <button onClick={() => { setFocoNotaOpen(false); setFocoNota('') }} style={removeBtn}>cancelar</button>
              </div>
              <p style={{ fontSize: '0.72rem', color: 'var(--muted)', margin: '0.5rem 0 0' }}>
                o número volta a zero, o gatilho fica. É ele que mostra o padrão.
              </p>
            </div>
          )}
        </motion.div>

        {/* ── HÁBITOS DO DIA ── 1 toque cada; o número é a sequência de dias seguidos até hoje */}
        <motion.div {...fade(0.065)} style={{ display: 'flex', gap: 6, marginBottom: '1rem' }}>
          {HABITOS_DIA.map(h => {
            const m = focoMap(habitos, h.key)
            const feito = m.get(date) === true
            const seq = focoStreak(m, todayStr())
            return (
              <button key={h.key} onClick={() => toggleHabito(h.key)} disabled={habSaving === h.key}
                aria-pressed={feito}
                style={{
                  flex: 1, padding: '0.7rem 0.5rem', borderRadius: 10, cursor: habSaving === h.key ? 'wait' : 'pointer',
                  border: '1px solid ' + (feito ? '#4ade80' : 'var(--border)'),
                  background: feito ? 'rgba(74,222,128,0.12)' : 'var(--bg2)',
                  color: feito ? 'var(--fg)' : 'var(--muted)', fontFamily: 'inherit', fontSize: '0.8rem', fontWeight: 600, transition: 'all .12s',
                }}>
                <span style={{ display: 'block' }}>{feito ? '✓ ' : ''}{h.label}</span>
                <span style={{ display: 'block', fontSize: '0.68rem', fontWeight: 500, color: 'var(--muted)', marginTop: 2 }}>{seq > 0 ? `${seq} dia${seq > 1 ? 's' : ''} seguido${seq > 1 ? 's' : ''}` : 'hoje conta'}</span>
              </button>
            )
          })}
        </motion.div>

        <motion.div {...fade(0.08)} style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12, padding: '1.5rem' }}>
          <label style={{ ...labelStyle, marginTop: 0 }}>Humor (1–10)</label>
          <Scale value={nivel} onChange={setNivel} accent="#4ade80" />
          <label style={labelStyle}>Energia (1–10)</label>
          <Scale value={energia} onChange={setEnergia} accent="#fbbf24" />

          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
            <div>
              <label style={labelStyle}>Sono (horas)</label>
              <input type="number" step="0.5" min="0" max="24" style={numStyle} value={sono} onChange={e => setSono(e.target.value)} placeholder="7.5" />
            </div>
            <div>
              <label style={labelStyle}>Exercício (horas)</label>
              <input type="number" step="0.25" min="0" max="12" style={numStyle} value={exercicio} onChange={e => setExercicio(e.target.value)} placeholder="1" />
            </div>
          </div>

          {/* ── TREINO ESTRUTURADO ── */}
          <label style={labelStyle}>Treino · toque pra adicionar sessão (academia + karatê = 2)</label>
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
            {TREINO_TIPOS.map(t => (
              <button key={t} onClick={() => addSessao(t)} style={{
                padding: '0.4rem 0.7rem', borderRadius: 8, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, textTransform: 'capitalize',
                border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--muted)',
              }}>+ {TIPO_LABEL[t] || t}</button>
            ))}
          </div>

          {hasStrength && (
            <div style={{ display: 'flex', gap: '1rem', marginTop: '0.85rem' }}>
              <div>
                <label style={{ ...labelStyle, marginTop: 0 }}>Bloco (1–4)</label>
                <input type="number" min="1" max="4" style={{ ...numStyle, width: 70 }} value={bloco} onChange={e => setBloco(e.target.value)} placeholder="1" />
              </div>
              <div>
                <label style={{ ...labelStyle, marginTop: 0 }}>Semana</label>
                <input type="number" min="1" max="8" style={{ ...numStyle, width: 70 }} value={semana} onChange={e => setSemana(e.target.value)} placeholder="1" />
              </div>
            </div>
          )}

          {sessoes.map((s, si) => (
            <div key={si} style={{ marginTop: '0.85rem', padding: '0.9rem', background: 'rgba(244,114,182,0.05)', border: '1px solid rgba(244,114,182,0.25)', borderRadius: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.6rem' }}>
                <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#f472b6', textTransform: 'capitalize' }}>{TIPO_LABEL[s.dia_tipo] || s.dia_tipo}</span>
                <button onClick={() => removeSessao(si)} style={removeBtn} aria-label="remover sessão">remover</button>
              </div>

              {WITH_EX.has(s.dia_tipo) && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '0.6rem' }}>
                  {s.exercicios.map((e, ei) => (
                    <div key={ei} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <input style={{ ...inputStyle, padding: '0.45rem 0.6rem', fontSize: '0.85rem' }} value={e.nome} onChange={ev => patchEx(si, ei, { nome: ev.target.value })} placeholder="exercício" />
                      <div style={{ display: 'flex', gap: 4 }}>
                        <input style={{ ...exNumStyle, width: 46 }} value={e.series} onChange={ev => patchEx(si, ei, { series: ev.target.value })} placeholder="sér" inputMode="numeric" />
                        <input style={{ ...exNumStyle, width: 70 }} value={e.reps} onChange={ev => patchEx(si, ei, { reps: ev.target.value })} placeholder="reps" />
                        <input style={{ ...exNumStyle, width: 64 }} value={e.carga_kg} onChange={ev => patchEx(si, ei, { carga_kg: ev.target.value })} placeholder="kg" inputMode="decimal" />
                        <input style={{ ...exNumStyle, width: 48 }} value={e.rpe} onChange={ev => patchEx(si, ei, { rpe: ev.target.value })} placeholder="rpe" inputMode="decimal" />
                        <button onClick={() => removeEx(si, ei)} style={{ ...removeBtn, width: 32, padding: 0, flexShrink: 0 }} aria-label="remover exercício">×</button>
                      </div>
                    </div>
                  ))}
                  <button onClick={() => addEx(si)} style={addExBtn}>+ exercício</button>
                </div>
              )}

              <input style={{ ...inputStyle, marginBottom: '0.4rem' }} value={s.obs} onChange={ev => patchSessao(si, { obs: ev.target.value })} placeholder={WITH_EX.has(s.dia_tipo) ? 'observações (ombro doendo, fiz metade…)' : 'o que foi? (ex: esteira 25min Z2, karatê leve)'} />
              <input type="number" step="10" min="0" max="5000" style={{ ...inputStyle, width: 110 }} value={s.calorias} onChange={ev => patchSessao(si, { calorias: ev.target.value })} placeholder="kcal" />
            </div>
          ))}

          {/* ── PR / MARCO ── */}
          {!prOpen ? (
            <button onClick={() => setPrOpen(true)} style={{ ...addExBtn, marginTop: '0.85rem' }}>🏆 registrar um PR (recorde)</button>
          ) : (
            <div style={{ marginTop: '0.85rem', padding: '0.9rem', background: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.3)', borderRadius: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.6rem' }}>
                <span style={{ fontWeight: 700, fontSize: '0.85rem', color: '#fbbf24' }}>🏆 PR / recorde</span>
                <button onClick={() => { setPrOpen(false); setPrEx(''); setPrValor(''); setPrCtx('') }} style={removeBtn}>cancelar</button>
              </div>
              <input style={{ ...inputStyle, marginBottom: '0.4rem' }} value={prEx} onChange={e => setPrEx(e.target.value)} placeholder="exercício (ex: Agachamento)" />
              <div style={{ display: 'flex', gap: 4, marginBottom: '0.4rem' }}>
                {PR_TIPOS.map(tp => (
                  <button key={tp} onClick={() => setPrTipo(tp)} style={{
                    flex: 1, padding: '0.45rem', borderRadius: 7, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600,
                    border: '1px solid ' + (prTipo === tp ? '#fbbf24' : 'var(--border)'), background: prTipo === tp ? '#fbbf24' : 'var(--bg2)', color: prTipo === tp ? '#0b0b0f' : 'var(--muted)',
                  }}>{tp}</button>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                <input type="number" step="2.5" style={{ ...inputStyle, width: 110 }} value={prValor} onChange={e => setPrValor(e.target.value)} placeholder="kg" inputMode="decimal" />
                <input style={{ ...inputStyle, flex: 1 }} value={prCtx} onChange={e => setPrCtx(e.target.value)} placeholder="contexto (opcional)" />
              </div>
            </div>
          )}

          <label style={labelStyle}>Nota do humor (opcional)</label>
          <input style={inputStyle} value={nota} onChange={e => setNota(e.target.value)} placeholder="ex: ansioso de manhã, melhorou à noite" />
          <label style={labelStyle}>Relato do dia</label>
          <textarea style={{ ...inputStyle, minHeight: 100, resize: 'vertical' }} value={relato} onChange={e => setRelato(e.target.value)} placeholder="o que rolou? o que pesou, o que foi bom…" />
          <label style={labelStyle}>Alimentação (só o que saiu do normal)</label>
          <input style={inputStyle} value={alimentacao} onChange={e => setAlimentacao(e.target.value)} placeholder="comeu normal? deixa vazio. besteira/doce/álcool/fora de hora? anota aqui" />
          <label style={labelStyle}>Ação pra amanhã (opcional)</label>
          <input style={inputStyle} value={acao} onChange={e => setAcao(e.target.value)} placeholder="1 coisa concreta" />
          <label style={labelStyle}>Âncora / frase do dia (opcional)</label>
          <input style={inputStyle} value={ancora} onChange={e => setAncora(e.target.value)} placeholder="algo pra lembrar" />

          <button onClick={submit} disabled={saving} style={{ marginTop: '1.5rem', width: '100%', padding: '0.85rem', borderRadius: 9, cursor: saving ? 'wait' : 'pointer', border: 'none', background: '#4ade80', color: '#0b0b0f', fontWeight: 700, fontSize: '0.95rem' }}>
            {saving ? 'salvando…' : (isToday ? 'Salvar check-in' : 'Salvar nesse dia')}
          </button>
          {msg && <p style={{ marginTop: '0.75rem', fontSize: '0.85rem', textAlign: 'center', color: msg.startsWith('✓') ? '#4ade80' : '#f87171' }}>{msg}</p>}
        </motion.div>

        {/* skeleton — só no 1º load de todos (sem cache ainda); depois o cache cobre */}
        {!loaded && treinos.length === 0 && humor.length === 0 && marcos.length === 0 && (
          <div style={{ marginTop: '2.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }} aria-hidden>
            {[0, 1, 2, 3].map(i => (
              <motion.div key={i} initial={{ opacity: 0.35 }} animate={{ opacity: [0.35, 0.65, 0.35] }} transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.1 }}
                style={{ height: 42, borderRadius: 8, background: 'var(--bg2)', border: '1px solid var(--border)' }} />
            ))}
          </div>
        )}

        {/* histórico de treinos */}
        {treinos.length > 0 && (
          <motion.section {...fade(0.16)} style={{ marginTop: '2.5rem' }}>
            <h2 style={{ fontSize: '0.72rem', color: 'var(--muted)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '0.85rem' }}>Treinos recentes</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {treinos.slice(0, 12).map((t, i) => (
                <button key={i} onClick={() => editTreino(t)} title="editar esse treino lá em cima" style={{
                  display: 'flex', alignItems: 'baseline', gap: '0.65rem', padding: '0.6rem 0.9rem', background: 'var(--bg2)',
                  border: '1px solid ' + (dayOf(t.data) === date ? '#f472b6' : 'var(--border)'), borderRadius: 8, fontSize: '0.8rem', cursor: 'pointer', textAlign: 'left',
                }}>
                  <span style={{ fontFamily: 'monospace', color: 'var(--muted)', minWidth: 60, flexShrink: 0 }}>{fmtDay(dayOf(t.data))}</span>
                  <span style={{ color: '#f472b6', fontWeight: 700, textTransform: 'capitalize', flexShrink: 0 }}>{t.dia_tipo}</span>
                  <span style={{ color: 'var(--fg)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{fmtTreino(t)}</span>
                  {t.rpe_medio != null && <span style={{ color: '#fbbf24', fontWeight: 600, flexShrink: 0 }}>RPE {t.rpe_medio}</span>}
                </button>
              ))}
            </div>
          </motion.section>
        )}

        {/* PRs */}
        {marcos.length > 0 && (
          <motion.section {...fade(0.2)} style={{ marginTop: '1.75rem' }}>
            <h2 style={{ fontSize: '0.72rem', color: 'var(--muted)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '0.75rem' }}>🏆 PRs</h2>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {marcos.slice(0, 12).map((m, i) => (
                <span key={i} style={{ padding: '0.4rem 0.7rem', background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.35)', borderRadius: 8, fontSize: '0.78rem', color: 'var(--fg)' }}>
                  <strong>{m.exercicio}</strong> {m.tipo} {m.valor}{m.unidade || 'kg'}
                </span>
              ))}
            </div>
          </motion.section>
        )}

        {/* vitais */}
        {humor.length > 0 && (
          <motion.section {...fade(0.24)} style={{ marginTop: '1.75rem' }}>
            <h2 style={{ fontSize: '0.72rem', color: 'var(--muted)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '0.85rem' }}>Últimos dias</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {humor.slice(0, 10).map((h, i) => (
                <button key={i} onClick={() => setDate(dayOf(h.data))} style={{
                  display: 'flex', alignItems: 'center', gap: '0.8rem', padding: '0.55rem 0.9rem', background: 'var(--bg2)', border: '1px solid ' + (dayOf(h.data) === date ? '#4ade80' : 'var(--border)'),
                  borderRadius: 8, fontSize: '0.8rem', cursor: 'pointer', textAlign: 'left',
                }}>
                  <span style={{ fontFamily: 'monospace', color: 'var(--muted)', minWidth: 64 }}>{fmtDay(dayOf(h.data))}</span>
                  {h.nivel != null && <span style={{ color: '#4ade80', fontWeight: 600 }}>♥{h.nivel}</span>}
                  {h.energia != null && <span style={{ color: '#fbbf24', fontWeight: 600 }}>⚡{h.energia}</span>}
                  {h.sono_horas != null && <span style={{ color: '#60a5fa', fontWeight: 600 }}>☾{h.sono_horas}h</span>}
                  {h.exercicio_horas != null && <span style={{ color: '#f472b6', fontWeight: 600 }}>🏋{h.exercicio_horas}h</span>}
                  {h.nota && <span style={{ color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.nota}</span>}
                </button>
              ))}
            </div>
          </motion.section>
        )}
      </main>
    </>
  )
}

const navBtn: React.CSSProperties = { width: 38, height: 38, borderRadius: 9, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--fg)', fontSize: '1.1rem', cursor: 'pointer', flexShrink: 0 }
const removeBtn: React.CSSProperties = { padding: '0.25rem 0.5rem', borderRadius: 6, border: '1px solid var(--border)', background: 'transparent', color: 'var(--muted)', fontSize: '0.72rem', cursor: 'pointer' }
const addExBtn: React.CSSProperties = { padding: '0.5rem', borderRadius: 7, border: '1px dashed var(--border)', background: 'transparent', color: 'var(--muted)', fontSize: '0.82rem', cursor: 'pointer', width: '100%' }
